import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ChatSidebar from "../../../components/Dashboard/CRM-AI/chatBot/ChatSidebar";
import ChatWindow from "../../../components/Dashboard/CRM-AI/chatBot/ChatWindow";
import ChatInput from "../../../components/Dashboard/CRM-AI/chatBot/ChatInput";
import { Menu } from "lucide-react";

import {
  createConversation,
  deleteConversation,
  getUserConversations,
  updateConversationTitle,
} from "../../../services/oprations/conversationAPI";
import { addMessage } from "../../../services/oprations/messageAPI";
import axios from "axios";

export default function ChatBot() {

  const { conversationId: routeConversationId } = useParams();
  const [chats, setChats] = useState([
    {
      title: "Welcome Chat",
      messages: [
        { role: "assistant", content: "Hi there! How can I help you today?" },
      ],
    },
  ]);
  const [activeChatIndex, setActiveChatIndex] = useState(0);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const user = useSelector((state) => state.profile?.user);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const sidebarRef = useRef();

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        isSidebarOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target)
      ) {
        setIsSidebarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isSidebarOpen]);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    const fetchChats = async () => {
      const fetchFn = getUserConversations();
      const conversations = await fetchFn();

      const formatted = conversations.map((conv) => ({
        conversationId: conv._id,
        title: conv.title,
        messages: conv.messages.map((msg) => ({
          role: msg.sender,
          content: msg.content,
          imageUrl: msg.imageUrl || null,
          webLinks: msg.webLinks || null,
          youtubeLinks: msg.youtubeLinks || null,
          actionButton: msg.actionButton || null,
        })),
      }));

      setChats(formatted);

      if (routeConversationId) {
        const index = formatted.findIndex(
          (chat) => chat.conversationId === routeConversationId
        );
        if (index !== -1) {
          setActiveChatIndex(index);
        } else {
          setActiveChatIndex(0); 
        }
      } else {
        setActiveChatIndex(0);
      }
    };

    fetchChats();
  }, [user, navigate, routeConversationId]);


  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessageContent = input;
    const updatedChats = [...chats];
    const currentChat = updatedChats[activeChatIndex];
    const conversationId = currentChat.conversationId || currentChat._id;

    currentChat.messages.push({ role: "user", content: userMessageContent });
    setChats([...updatedChats]);
    setInput("");
    setIsLoading(true);

    try {
      const payload = {
        session_id: conversationId,
        query: userMessageContent,
        credentials: { crm: "odoo" }
      };

      const res = await axios.post(
        `${process.env.REACT_APP_AI_BACKEND_URL}/crm/query`, 
        payload
      );

      // Keep the text clean so it doesn't show raw HTML code on screen
      let assistantContent = res.data?.final || "I couldn't retrieve the data from Odoo.";
      
      // Separate the button data from the text content
      const actionButton = (res.data?.intent === "EMAIL" && res.data?.tool_result?.action_button) 
        ? res.data.tool_result.action_button 
        : null;

      const assistantMessage = {
        role: "assistant",
        content: assistantContent,
        imageUrl: res.data.imageUrl || null,
        webLinks: res.data.webLinks || null,
        youtubeLinks: res.data.youtubeLinks || null,
        actionButton: actionButton // Pass the button object here
      };

      // Sync to Database
      const addUserMessage = addMessage(conversationId, "user", userMessageContent)();
      const addBotMessage = addMessage(
        conversationId,
        "assistant",
        assistantContent,
        assistantMessage.imageUrl,
        assistantMessage.webLinks,
        assistantMessage.youtubeLinks,
        assistantMessage.actionButton // Ensure your API saves this field
      )();

      await Promise.all([addUserMessage, addBotMessage]);

      currentChat.messages.push(assistantMessage);
      setChats([...updatedChats]);

    } catch (error) {
      console.error("Odoo Query Error:", error);
      currentChat.messages.push({
        role: "assistant",
        content: "Error: I was unable to connect to your Odoo instance.",
      });
      setChats([...updatedChats]);
    } finally {
      setIsLoading(false);
    }
  };

  const generateChatName = (existingChats) => {
    const base = "Chat";
    let index = 1;
    while (existingChats.some((chat) => chat.title === `${base} ${index}`)) {
      index++;
    }
    return `${base} ${index}`;
  };

  const handleNewChat = async () => {
    try {
      const chatName = generateChatName(chats);
      const newConversation = await dispatch(createConversation(chatName, ""));

      if (!newConversation) return;

      const formatted = {
        conversationId: newConversation._id,
        title: newConversation.title || chatName,
        messages: newConversation.messages.map((msg) => ({
          role: msg.sender,
          content: msg.content,
        })),
      };

      setChats([...chats, formatted]);
      setActiveChatIndex(chats.length);
    } catch (error) {
      console.error("Failed to create new conversation:", error);
    }
  };

  const handleSelectChat = (index) => {
    setActiveChatIndex(index);
    setIsSidebarOpen(false);
  };

  const handleEditTitle = async (conversationId, newTitle) => {
    try {
      const updateFn = updateConversationTitle(conversationId, newTitle);
      const updatedConversation = await updateFn();

      if (updatedConversation) {
        setChats((prevChats) =>
          prevChats.map((chat) =>
            chat.conversationId === conversationId
              ? { ...chat, title: updatedConversation.title }
              : chat
          )
        );
      }
    } catch (err) {
      console.error("Failed to update title:", err);
    } finally {
      setEditingIndex(null);
    }
  };


  const handleDeleteChat = async (conversationId) => {
    try {
      const deleteFn = deleteConversation(conversationId);
      const success = await deleteFn();

      if (success) {
        setChats((prevChats) => {
          const filtered = prevChats.filter(
            (chat) => chat.conversationId !== conversationId
          );
          const newActiveIndex = Math.max(
            0,
            prevChats.findIndex((chat) => chat.conversationId === conversationId) - 1
          );
          setActiveChatIndex(newActiveIndex);
          return filtered;
        });
      }
    } catch (error) {
      console.error("Failed to delete conversation:", error);
    }
  };


  return (
    <div className="flex min-h-[100dvh] sm:h-screen bg-gray-900 text-white relative">
      {/* Desktop Sidebar */}
      <div className="hidden md:block w-64 border-r border-gray-700">
        <ChatSidebar
          chats={chats}
          onNewChat={handleNewChat}
          onSelectChat={handleSelectChat}
          onDeleteChat={handleDeleteChat}
          onEditTitle={handleEditTitle}
          activeChatIndex={activeChatIndex}
          editingIndex={editingIndex}
          setEditingIndex={setEditingIndex}
        />
      </div>

      {/* Mobile Sidebar */}
      {isSidebarOpen && (
        <div className="absolute inset-0 z-50 bg-opacity-30 md:hidden">
          <div
            ref={sidebarRef}
            className="w-64 h-full bg-gray-800 border-r border-gray-700"
          >
            <ChatSidebar
              chats={chats}
              onNewChat={handleNewChat}
              onSelectChat={handleSelectChat}
              onDeleteChat={handleDeleteChat}
              onEditTitle={handleEditTitle}
              activeChatIndex={activeChatIndex}
              editingIndex={editingIndex}
              setEditingIndex={setEditingIndex}
            />
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col h-screen">
        {/* Mobile Nav */}
        <div className="md:hidden p-4 flex items-center justify-between border-b border-gray-700">
          <button onClick={() => setIsSidebarOpen(true)}>
            <Menu className="w-6 h-6 text-white" />
          </button>
          {/* <Navbar /> */}
        </div>

        {/* Desktop Nav */}
        <div className=" flex-shrink-0 hidden md:block">
          {/* <Navbar /> */}
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          <ChatWindow
            messages={chats[activeChatIndex]?.messages || []}
            isLoading={isLoading}
          />
        </div>
        {chats.length > 0 ? (
          <div className="flex-shrink-0">
            <ChatInput
              input={input}
              setInput={setInput}
              handleSend={handleSend}
              setChats={setChats}
              chats={chats}
              addMessage={addMessage}
              activeChatIndex={activeChatIndex}
              isLoading={isLoading}
            />
          </div>
        ) : (
          <div className="p-4 text-center text-gray-400 border-t border-gray-700">
            No Conversation found
          </div>
        )}
      </div>
    </div>
  );
}